import React, { useState, useEffect } from 'react';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import PageContainer from '../../components/PageElements/PageContainer/PageContainer';
import PageBody from '../../components/PageElements/PageBody/PageBody';
import { usePageSEO } from '../../hooks/usePageSEO';
import styles from './PartnerProgram.module.css';
import buffcaulImage from '../../images/buffcaul.jpg';
import chickpeatacoImage from '../../images/chickpeataco.jpg';
import pumpkinSoupImage from '../../images/pumpkin_soup_8.jpg';
import smorecookiesImage from '../../images/smorecookies.jpg';
import chickpeasaladImage from '../../images/chickpeasalad.png';
import pastactpImage from '../../images/pastactp.jpg';
import chartsWidgetsImage from '../../images/ChartsWidgets.png';
import dataExplorerImage from '../../images/DataExplorer.png';

const PartnerProgram: React.FC = () => {
  const [activeDashboard, setActiveDashboard] = useState(0);

  const dashboards = [
    {
      id: 'charts',
      title: 'Visitor Analytics Dashboard',
      subtitle: 'Track customer dietary preferences and health concerns',
      image: chartsWidgetsImage,
      alt: 'Charts and widgets showing visitor analytics'
    },
    {
      id: 'data-explorer',
      title: 'Customer Data Explorer',
      subtitle: 'Detailed insights into your customer base',
      image: dataExplorerImage,
      alt: 'Data explorer showing customer insights'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDashboard((prev) => (prev + 1) % dashboards.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [dashboards.length]);

  const goToDashboard = (index: number) => {
    setActiveDashboard(index);
  };

  // SEO hook for the smart dining initiative page
  usePageSEO({
    title: "Smart Dining Initiative | PlatefullAI - Free Website Design for Restaurants",
    description: "Get a free website designed by Just Cr8if LLC with PlatefullAI's personalized menu technology. Transform your restaurant's online presence with AI-powered dietary personalization.",
    keywords: "restaurant website design, free website, menu personalization, dietary preferences, restaurant technology, AI-powered menus, Cr8if LLC",
    canonicalUrl: "https://platefullai.com/smart-dining-initiative"
  });

  return (
    <PageContainer>      
      <PortalHeader 
        showContentHeader={false}
      />
      <PageBody>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <h1 className={styles.heroTitle}>Smart Dining Initiative</h1>
              <p className={styles.heroDescription}>Launch or revamp your website for free—your design, our technology. PlatefullAI gives your customers a more personalized experience with embedded menu intelligence. This is the future of food on the internet. Designed and delivered with <a href="https://www.justcr8if.com/" target="_blank" rel="noopener noreferrer" className={styles.link}>Just Cr8if LLC</a>.</p>
              <div className={styles.heroButtons}>
                <a
                  className={styles.ctaButton}
                  href={'mailto:james@justcr8if.com?subject=' + encodeURIComponent('Smart Dining Initiative') + '&body=' + encodeURIComponent("Hey! We're interested in the Smart Dining Initiative!")}
                >
                  Apply to partner
                </a>
                <a
                  className={styles.pricingButton}
                  href="/pricing"
                >
                  View Pricing
                </a>
              </div>
            </div>
            <div className={styles.heroRight}>
              <div className={styles.imageGrid}>
                <img src={buffcaulImage} alt="Buffalo Cauliflower" className={`${styles.foodImage} ${styles.image1}`} />
                <img src={chickpeatacoImage} alt="Chickpea Taco" className={`${styles.foodImage} ${styles.image2}`} />
                <img src={pumpkinSoupImage} alt="Pumpkin Soup" className={`${styles.foodImage} ${styles.image3}`} />
                <img src={smorecookiesImage} alt="S'more Cookies" className={`${styles.foodImage} ${styles.image4}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Personalized Menu Demo Section */}
        <div className={styles.demoSection}>
          <div className={styles.demoContent}>
            <h2 className={styles.demoTitle}>See the Future in Action</h2>
            <p className={styles.demoSubtitle}>Your menu will be interactive to users, depending on who's viewing it. Hover over highlighted ingredients below.</p>
            
            <div className={styles.menuDemo}>
              <div className={styles.dietIndicator}>
                <span className={styles.dietLabel}>User is Vegan</span>
              </div>
              <div className={styles.menuImageContainer}>
                <img src={chickpeasaladImage} alt="Chickpea Salad" className={styles.menuImage} />
              </div>
              
              <div className={styles.menuDescription}>
                <h3 className={styles.menuItemTitle}>
                  <span className={styles.highlightedTitle}>Mediterranean Chickpea Salad</span>
                </h3>
                <p className={styles.menuItemDescription}>
                  Fresh <span className={styles.highProtein} title="High Protein">chickpeas</span> and quinoa, 
                   cucumber, tomatoes, and red onion. 
                  Served with our <span className={styles.containsNuts} title="Contains Nuts">spicy cashew sauce</span>.
                </p>
                <div className={styles.dietaryTags}>
                  <span className={`${styles.tag} ${styles.highProtein}`}>High Protein</span>
                  <span className={`${styles.tag} ${styles.containsNuts}`}>Contains Nuts</span>
                </div>
              </div>
            </div>

            {/* Second Menu Demo - High Blood Pressure */}
            <div className={styles.menuDemo}>
              <div className={styles.dietIndicator}>
                <span className={`${styles.dietLabel}`}>User with a High Blood Pressure Concern</span>
              </div>
              <div className={styles.menuImageContainer}>
                <img src={pastactpImage} alt="Pasta with Chicken" className={styles.menuImage} />
              </div>
              
              <div className={styles.menuDescription}>
                <h3 className={styles.menuItemTitle}>
                  <span className={styles.highlightedTitle}>Creamy Chicken Pasta</span>
                </h3>
                <p className={styles.menuItemDescription}>
                  Tender <span className={styles.highSodium} title="High Sodium">chicken breast</span> with <span className={styles.highSodium} title="High Sodium">sun-dried tomatoes</span>, 
                  fresh spinach, and <span className={styles.highSodium} title="High Sodium">creamy alfredo sauce</span> served over pasta. 
                  Garnished with <span className={styles.highSodium} title="High Sodium">parmesan cheese</span> and herbs.
                </p>
                <div className={styles.dietaryTags}>
                  <span className={`${styles.tag} ${styles.highSodium}`}>High Sodium</span>
                </div>
              </div>
            </div>

            {/* Business Analytics Section */}
            <div className={styles.businessAnalyticsSection}>
              <h2 className={styles.sectionTitle}>Track More Than Just Visits</h2>
              <p className={styles.sectionDescription}>
                Get detailed insights into your customers' dietary preferences and health concerns. 
                See exactly what your visitors eat and how to better serve them.
              </p>
              
              <div className={styles.analyticsShowcase}>
                <div className={styles.dashboardCarousel}>
                  <div className={styles.carouselContainer}>
                    {dashboards.map((dashboard, index) => (
                      <div 
                        key={dashboard.id} 
                        className={`${styles.carouselSlide} ${activeDashboard === index ? styles.active : ''}`}
                      >
                        <div className={styles.analyticsScreenshot}>
                          <div className={styles.screenshotHeader}>
                            <div className={styles.screenshotTitle}>{dashboard.title}</div>
                            <div className={styles.screenshotSubtitle}>{dashboard.subtitle}</div>
                          </div>
                          <div className={styles.screenshotContent}>
                            <img 
                              src={dashboard.image} 
                              alt={dashboard.alt} 
                              className={styles.dashboardImage}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Navigation arrows */}
                    <button 
                      className={styles.carouselArrow} 
                      onClick={() => goToDashboard(activeDashboard === 0 ? dashboards.length - 1 : activeDashboard - 1)}
                      aria-label="Previous dashboard"
                    >
                      ‹
                    </button>
                    <button 
                      className={`${styles.carouselArrow} ${styles.carouselArrowRight}`} 
                      onClick={() => goToDashboard((activeDashboard + 1) % dashboards.length)}
                      aria-label="Next dashboard"
                    >
                      ›
                    </button>
                  </div>
                  
                  {/* Dot indicators */}
                  <div className={styles.carouselIndicators}>
                    {dashboards.map((_, index) => (
                      <button
                        key={index}
                        className={`${styles.carouselDot} ${activeDashboard === index ? styles.active : ''}`}
                        onClick={() => goToDashboard(index)}
                        aria-label={`Go to dashboard ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className={styles.analyticsFeatures}>
                  <div className={styles.featureItem}>
                    <div className={styles.featureIcon}>📊</div>
                    <h3>Real-time Analytics</h3>
                    <p>Track customer dietary preferences and health concerns as they visit your venue</p>
                  </div>
                  <div className={styles.featureItem}>
                    <div className={styles.featureIcon}>👥</div>
                    <h3>Customer Insights</h3>
                    <p>Understand your customer base with detailed diet profiles and preferences</p>
                  </div>
                  <div className={styles.featureItem}>
                    <div className={styles.featureIcon}>📈</div>
                    <h3>Trend Analysis</h3>
                    <p>Identify popular dietary trends and adapt your menu accordingly</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Program Details Section */}
            <div className={styles.programDetails}>
              <div className={styles.programContent}>
                <h2 className={styles.programTitle}>How It Works</h2>
                
                <div className={styles.detailsGrid}>
                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>Application Process</h3>
                    <p className={styles.detailText}>
                      Simply email us expressing your interest. Tell us what type of restaurant you operate and how long you've been in business. 
                      We'll review based on our internal requirements, which are flexible and designed to ensure mutual success.
                    </p>
                  </div>

                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>Two-Year Partnership</h3>
                    <p className={styles.detailText}>
                      We commit to maintaining your website, making updates, and providing ongoing support for two years. 
                      You can withdraw at any time if needed. This partnership ensures your technology stays current and functional.
                    </p>
                  </div>

                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>Self-Management Tools</h3>
                    <p className={styles.detailText}>
                      We'll build you a management system that lets you update menus, change content, and manage your restaurant 
                      information without any coding knowledge. You maintain control while we handle the technical complexity.
                    </p>
                  </div>

                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>Integration Support</h3>
                    <p className={styles.detailText}>
                      We'll add support for DoorDash, Uber Eats, or any other delivery platform you need. 
                      Your website will seamlessly connect with your existing business operations and third-party services.
                    </p>
                  </div>

                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>Customer Analytics</h3>
                    <p className={styles.detailText}>
                      Get authentication through PlatefullAI to see who's visiting your site daily. 
                      Track customer preferences, dietary needs, and engagement patterns to better understand your audience.
                    </p>
                  </div>

                  <div className={styles.detailCard}>
                    <h3 className={styles.detailTitle}>On-Site Check-In</h3>
                    <p className={styles.detailText}>
                      We'll ship QR codes or NFC tags to your restaurant. Customers can check in on-site, 
                      allowing your servers to see their favorites and preferences for a personalized dining experience.
                    </p>
                  </div>
                </div>

                <div className={styles.commitmentNote}>
                  <p className={styles.commitmentText}>
                    This is a two-year commitment where we'll be there for you to help with the website or anything we can. 
                    It means you'll give us feedback and help us make the best experience for the user. 
                    We're looking for partners who want to be at the forefront of how restaurants interact with customers online.
                  </p>
                </div>

                <div className={styles.bottomCta}>
                  <a
                    className={styles.ctaButton}
                    href={'mailto:james@justcr8if.com?subject=' + encodeURIComponent('Smart Dining Initiative') + '&body=' + encodeURIComponent("Hey! We're interested in the Smart Dining Initiative!")}
                  >
                    Apply to partner
                  </a>
                  <a
                    className={styles.pricingButton}
                    href="/pricing"
                  >
                    View Pricing
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageBody>
    </PageContainer>
  );
};

export default PartnerProgram;
